import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

type Primitive = string | number | boolean | undefined | null;

const serialize = (value: Primitive) => {
  if (value === undefined || value === null || value === "") return null;
  return String(value);
};

type Options = {
  defaultActiveTab?: string;
};

type HistoryMode = "push" | "replace";

export const useSearchParams = (options?: Options) => {
  const history = useHistory();
  const location = useLocation();

  const getParams = () => new URLSearchParams(location.search);

  const commit = (params: URLSearchParams, mode: HistoryMode = "replace") => {
    history[mode]({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  /**
   * active-tab (with default)
   */
  const activeTab = useMemo(() => {
    return getParams().get("active-tab") ?? options?.defaultActiveTab;
  }, [location.search, options?.defaultActiveTab]);

  /**
   * ensure default active-tab
   * runs once when hook is mounted
   */
  useEffect(() => {
    if (!options?.defaultActiveTab) return;

    const params = getParams();

    if (!params.has("active-tab")) {
      params.set("active-tab", options.defaultActiveTab);
      commit(params, "replace");
    }
  }, []);

  /**
   * set active tab + clear URL
   */
  const setActiveTab = (tab: string) => {
    const params = new URLSearchParams();
    params.set("active-tab", tab);

    commit(params, "push");
  };

  /**
   * table state (tab.table.key)
   */
  const setTableState = (
    tab: string,
    table: string,
    state: Record<string, Primitive>,
    mode: HistoryMode = "replace"
  ) => {
    const params = getParams();

    Object.entries(state).forEach(([key, value]) => {
      const serialized = serialize(value);
      const paramKey = `${tab}.${table}.${key}`;

      if (serialized === null) {
        params.delete(paramKey);
      } else {
        params.set(paramKey, serialized);
      }
    });

    commit(params, mode);
  };

  const getTableState = <T extends Record<string, Primitive>>(
    tab: string,
    table: string,
    defaults: T
  ): T => {
    const params = getParams();
    const result: T = { ...defaults };

    (Object.keys(defaults) as Array<keyof T>).forEach((key) => {
      const value = params.get(`${tab}.${table}.${String(key)}`);
      if (value === null) return;

      const defaultValue = defaults[key];
      if (typeof defaultValue === "number") {
        result[key] = Number(value) as T[keyof T];
      } else if (typeof defaultValue === "boolean") {
        result[key] = (value === "true") as T[keyof T];
      } else {
        result[key] = value as T[keyof T];
      }
    });

    return result;
  };

  return {
    params: getParams(),
    activeTab,
    setActiveTab,
    setTableState,
    getTableState,
  };
};
